/**
 * Staff diagnose tool for Pulse events.
 *
 * Paste an event UUID, get a structured report: event envelope, every
 * subscription on the same (tenant, event_key), filter trace per
 * subscription, and the live status of every delivery created from
 * the event. Modelled after /staff/triggers/diagnose.
 *
 * Accepts ?event_uuid=... so the failures table on the overview page
 * can deep-link straight into a diagnose without re-pasting.
 */

import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { AlertTriangle, ArrowLeft, CheckCircle2, Loader2 } from 'lucide-react'

import api from '@/admin/lib/api'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import { StaffShell } from '@/admin/components/staff/staff-shell'
import { toastApiError } from '@/shared/lib/api-error'
import { cn } from '@/shared/lib/utils'

interface DiagnoseReport {
  event: {
    uuid: string
    event_key: string
    tenant: { id: number; slug: string; name: string }
    organisation_id: string | null
    api_version: string
    created_at: string
    processed_at: string | null
    payload: unknown
  }
  subscription_count: number
  matched_count: number
  subscriptions: SubscriptionReport[]
  deliveries: DeliveryRow[]
}

interface SubscriptionReport {
  subscription_uuid: string
  consumer_kind: string
  is_active: boolean
  description: string
  target_url: string | null
  filter: Record<string, unknown>
  matched: boolean
  filter_trace: ClauseTrace[]
}

interface ClauseTrace {
  path: string
  op: string
  expected: unknown
  actual: unknown
  matched: boolean
}

interface DeliveryRow {
  delivery_uuid: string
  subscription_uuid: string
  status: string
  attempt_generation: number
  created_at: string
  delivered_at: string | null
  last_attempt_at: string | null
}

export function StaffPulseDiagnosePage() {
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const [eventUuid, setEventUuid] = useState(params.get('event_uuid') ?? '')
  const [loading, setLoading] = useState(false)
  const [report, setReport] = useState<DiagnoseReport | null>(null)

  async function run(uuid: string) {
    const trimmed = uuid.trim()
    if (!trimmed) return
    setLoading(true)
    setReport(null)
    try {
      const res = await api.post('/staff/pulse/diagnose/', {
        event_uuid: trimmed,
      })
      setReport((res.data?.result ?? res.data) as DiagnoseReport)
      setParams({ event_uuid: trimmed }, { replace: true })
    } catch (err) {
      toastApiError(err, 'Diagnose failed.')
    } finally {
      setLoading(false)
    }
  }

  // Deep-link from the overview's failure table: run automatically
  // when the query param is present on first mount.
  useEffect(() => {
    const fromUrl = params.get('event_uuid')
    if (fromUrl) run(fromUrl)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <StaffShell>
    <div className="mx-auto mt-8 max-w-[1100px] px-6 pb-12">
      <button
        type="button"
        onClick={() => navigate('/staff/pulse')}
        className="mb-3 inline-flex cursor-pointer items-center gap-1 text-[11px] text-[var(--ink-3)] hover:text-[var(--ink-2)]"
      >
        <ArrowLeft className="h-3 w-3" />
        Back to Pulse
      </button>
      <h1 className="font-display text-[28px] leading-tight text-[var(--ink)]">
        Diagnose event
      </h1>
      <p className="mt-0.5 max-w-[640px] text-[12px] text-[var(--ink-3)]">
        Paste a Pulse event UUID. Returns the event envelope, every
        subscription on the same (tenant, event_key) at fire time, the
        per-clause filter trace, and the live status of every delivery
        created from the event.
      </p>

      {/* Input */}
      <div className="mt-5 flex items-end gap-2">
        <div className="flex-1">
          <Label htmlFor="evt" className="text-[11.5px]">
            Event UUID
          </Label>
          <Input
            id="evt"
            value={eventUuid}
            onChange={(e) => setEventUuid(e.target.value)}
            placeholder="fd9dc2fc-8485-4eb9-95d8-4bda85dff8a5"
            className="mt-1.5 font-mono text-[12px]"
            onKeyDown={(e) => {
              if (e.key === 'Enter') run(eventUuid)
            }}
          />
        </div>
        <Button onClick={() => run(eventUuid)} disabled={loading || !eventUuid.trim()}>
          {loading && <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />}
          Diagnose
        </Button>
      </div>

      {/* Report */}
      {report && (
        <div className="mt-6 space-y-5">
          <EventCard report={report} />
          <SubscriptionsCard report={report} />
          <DeliveriesCard report={report} />
        </div>
      )}
    </div>
    </StaffShell>
  )
}

// ---------------------------------------------------------------------------
// Report cards
// ---------------------------------------------------------------------------

function EventCard({ report }: { report: DiagnoseReport }) {
  const e = report.event
  return (
    <Card title="Event">
      <KV label="Event key">
        <code className="font-mono text-[12px] text-[var(--ink-2)]">
          {e.event_key}
        </code>
      </KV>
      <KV label="Event UUID">
        <code className="font-mono text-[11.5px] text-[var(--ink-3)]">
          {e.uuid}
        </code>
      </KV>
      <KV label="Tenant">
        <span className="text-[12.5px]">
          {e.tenant.name}{' '}
          <span className="font-mono text-[11px] text-[var(--ink-3)]">
            {e.tenant.slug}
          </span>
        </span>
      </KV>
      {e.organisation_id && (
        <KV label="Organisation">
          <code className="font-mono text-[11.5px] text-[var(--ink-3)]">
            {e.organisation_id}
          </code>
        </KV>
      )}
      <KV label="Created">
        <span className="font-mono text-[11.5px]">{e.created_at}</span>
      </KV>
      {e.processed_at && (
        <KV label="Drained">
          <span className="font-mono text-[11.5px]">{e.processed_at}</span>
        </KV>
      )}
      <KV label="API version">
        <code className="font-mono text-[11px]">{e.api_version}</code>
      </KV>
      <details className="mt-2 border-t-[0.5px] border-t-[var(--rule-light)] px-1 pt-2">
        <summary className="cursor-pointer text-[10px] uppercase tracking-[0.16em] text-[var(--ink-3)]">
          Payload
        </summary>
        <pre className="mt-2 max-h-[260px] overflow-auto whitespace-pre-wrap break-all rounded-[4px] bg-[var(--bg-subtle)] p-2.5 font-mono text-[10.5px] leading-snug text-[var(--ink-2)]">
          {JSON.stringify(e.payload, null, 2)}
        </pre>
      </details>
    </Card>
  )
}

function SubscriptionsCard({ report }: { report: DiagnoseReport }) {
  if (report.subscription_count === 0) {
    return (
      <Card title="Subscriptions">
        <p className="px-2 py-4 text-center text-[12px] text-[var(--ink-3)]">
          No subscriptions on this tenant + event_key.
        </p>
      </Card>
    )
  }
  return (
    <Card
      title={`Subscriptions (${report.matched_count} of ${report.subscription_count} matched)`}
    >
      <div className="divide-y-[0.5px] divide-[var(--rule-light)]">
        {report.subscriptions.map((s) => (
          <SubscriptionRow key={s.subscription_uuid} sub={s} />
        ))}
      </div>
    </Card>
  )
}

function SubscriptionRow({ sub }: { sub: SubscriptionReport }) {
  const filterCount = Object.keys(sub.filter ?? {}).length
  return (
    <div className="px-4 py-3">
      <div className="mb-1.5 flex items-center gap-2">
        {sub.matched ? (
          <Badge
            variant="outline"
            className="border-[var(--green-border)] bg-[var(--green-bg)] text-[var(--green)]"
          >
            <CheckCircle2 className="mr-1 h-3 w-3" /> Matched
          </Badge>
        ) : (
          <Badge variant="destructive">
            <AlertTriangle className="mr-1 h-3 w-3" /> Did not match
          </Badge>
        )}
        {!sub.is_active && (
          <Badge variant="outline" className="text-[var(--ink-3)]">
            Inactive
          </Badge>
        )}
        <Badge variant="outline" className="text-[var(--ink-3)]">
          {sub.consumer_kind}
        </Badge>
      </div>
      <div className="text-[12.5px] text-[var(--ink)]">
        {sub.description || 'Untitled subscription'}
      </div>
      {sub.target_url && (
        <div className="truncate font-mono text-[11px] text-[var(--ink-3)]">
          {sub.target_url}
        </div>
      )}
      <div className="mt-1 truncate font-mono text-[10.5px] text-[var(--ink-3)]">
        sub_{sub.subscription_uuid}
      </div>
      {filterCount === 0 ? (
        <p className="mt-2 text-[11px] text-[var(--ink-3)]">
          No filter - this subscription matches every occurrence.
        </p>
      ) : (
        <div className="mt-2 rounded-[4px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] p-2">
          <div className="mb-1 text-[10px] uppercase tracking-[0.16em] text-[var(--ink-3)]">
            Filter trace ({filterCount} clause{filterCount === 1 ? '' : 's'})
          </div>
          {sub.filter_trace.map((c, i) => (
            <ClauseLine key={i} clause={c} />
          ))}
        </div>
      )}
    </div>
  )
}

function ClauseLine({ clause }: { clause: ClauseTrace }) {
  return (
    <div className="flex items-start gap-2 py-1 text-[11.5px]">
      <span
        className={cn(
          'mt-1 inline-block h-1.5 w-1.5 shrink-0 rounded-full',
          clause.matched ? 'bg-[var(--green)]' : 'bg-[var(--red)]',
        )}
      />
      <div className="min-w-0 flex-1">
        <div className="font-mono text-[11.5px] text-[var(--ink-2)]">
          {clause.path}{' '}
          <span className="text-[var(--ink-3)]">{clause.op}</span>{' '}
          <code className="text-[var(--ink)]">
            {JSON.stringify(clause.expected)}
          </code>
        </div>
        <div className="font-mono text-[10.5px] text-[var(--ink-3)]">
          envelope had:{' '}
          <code className="text-[var(--ink-2)]">
            {JSON.stringify(clause.actual)}
          </code>
        </div>
      </div>
    </div>
  )
}

function DeliveriesCard({ report }: { report: DiagnoseReport }) {
  if (report.deliveries.length === 0) {
    return (
      <Card title="Deliveries">
        <p className="px-2 py-4 text-center text-[12px] text-[var(--ink-3)]">
          No deliveries created from this event. Either no subscription
          matched, or the drain hasn&apos;t run yet.
        </p>
      </Card>
    )
  }
  return (
    <Card title={`Deliveries (${report.deliveries.length})`}>
      <table className="w-full text-[12px]">
        <thead className="border-b-[0.5px] border-[var(--rule)]">
          <tr className="text-left text-[10px] uppercase tracking-[0.16em] text-[var(--ink-3)]">
            <th className="px-4 py-2 font-normal">Delivery</th>
            <th className="px-4 py-2 font-normal">Status</th>
            <th className="px-4 py-2 font-normal">Gen</th>
            <th className="px-4 py-2 font-normal">Created</th>
            <th className="px-4 py-2 font-normal">Last attempt</th>
            <th className="px-4 py-2 font-normal">Delivered</th>
          </tr>
        </thead>
        <tbody>
          {report.deliveries.map((d) => (
            <tr
              key={d.delivery_uuid}
              className="border-t-[0.5px] border-t-[var(--rule-light)]"
            >
              <td className="px-4 py-2 font-mono text-[10.5px] text-[var(--ink-3)]">
                del_{d.delivery_uuid.slice(0, 8)}
              </td>
              <td className="px-4 py-2">
                <StatusBadge status={d.status} />
              </td>
              <td className="px-4 py-2 font-mono text-[11px] text-[var(--ink)]">
                {d.attempt_generation}
              </td>
              <td className="px-4 py-2 font-mono text-[10.5px] text-[var(--ink-3)]">
                {d.created_at}
              </td>
              <td className="px-4 py-2 font-mono text-[10.5px] text-[var(--ink-3)]">
                {d.last_attempt_at ?? '-'}
              </td>
              <td className="px-4 py-2 font-mono text-[10.5px] text-[var(--ink-3)]">
                {d.delivered_at ?? '-'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  )
}

function StatusBadge({ status }: { status: string }) {
  if (status === 'DELIVERED')
    return (
      <Badge
        variant="outline"
        className="border-[var(--status-ok-border)] bg-[var(--status-ok-bg)] text-[var(--status-ok)]"
      >
        Delivered
      </Badge>
    )
  if (status === 'PENDING')
    return (
      <Badge
        variant="outline"
        className="border-[var(--gold-border)] bg-[var(--gold-bg)] text-[var(--gold-ink)]"
      >
        Pending
      </Badge>
    )
  if (status === 'SUPPRESSED')
    return (
      <Badge variant="outline" className="text-[var(--ink-3)]">
        Suppressed
      </Badge>
    )
  return <Badge variant="destructive">{status}</Badge>
}

// ---------------------------------------------------------------------------
// chrome
// ---------------------------------------------------------------------------

function Card({
  title,
  children,
}: {
  title: string
  children: React.ReactNode
}) {
  return (
    <section>
      <h2 className="mb-2 text-[10px] font-normal uppercase tracking-[0.16em] text-[var(--ink-3)]">
        {title}
      </h2>
      <div className="overflow-hidden rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)]">
        {children}
      </div>
    </section>
  )
}

function KV({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[140px_1fr] items-start gap-3 border-b-[0.5px] border-[var(--rule-light)] px-4 py-2 last:border-b-0">
      <div className="pt-0.5 text-[10px] uppercase tracking-[0.16em] text-[var(--ink-3)]">
        {label}
      </div>
      <div className="text-[12px] text-[var(--ink)]">{children}</div>
    </div>
  )
}
